import {
  FailedGroup,
  FailedMediaListModal,
  FailedRow,
} from '@components/FailedMediaList/FailedMediaListModal';
import { getFileThumbnailUrl } from '@lib/hooks/useFileMetadataExtraction';
import { BatchNotification, isBatchNotification, notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

interface UploadResultsModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, only show failures for this batch. When omitted, show all. */
  filterNotificationId?: string;
  /** Called when the filter batch is emptied — parent should drop to unfiltered. */
  onFilterBatchEmpty?: () => void;
  /** Retry all failed files matching the current filter. */
  onRetryAll?: (notificationId?: string) => void;
}

export const UploadResultsModal = observer(function UploadResultsModal({
  open,
  onClose,
  filterNotificationId,
  onFilterBatchEmpty,
  onRetryAll,
}: UploadResultsModalProps) {
  const uploadManager = useUploadManager();

  const batchNotifications = notificationStore.notifications.filter(isBatchNotification);

  // Compute filter-batch presence during render so observer() tracks the read
  // on notificationStore.notifications. Reads inside a useEffect callback are
  // NOT proxied by observer — only render-time reads are — so an effect-based
  // check would never re-run when the filtered batch was removed mid-modal.
  const filterBatchExists = filterNotificationId
    ? notificationStore.notifications.some((n) => n.id === filterNotificationId)
    : true;

  useEffect(() => {
    if (open && filterNotificationId && !filterBatchExists) {
      onFilterBatchEmpty?.();
    }
  }, [open, filterNotificationId, filterBatchExists, onFilterBatchEmpty]);

  const scoped = filterNotificationId
    ? batchNotifications.filter((n) => n.id === filterNotificationId)
    : batchNotifications;

  const groups: FailedGroup[] = [];
  for (const n of scoped) {
    const failures = uploadManager.getBatchFailures(n);
    const rows: FailedRow[] = [];

    failures.pendingUploadFailures.forEach((r, i) => {
      rows.push({
        key: `${n.id}:upload:${i}`,
        filename: r.file.name,
        reason: r.reason,
        status: 'failed',
        isAudio: n.isAudio,
        thumbnailUrl: getFileThumbnailUrl(r.file),
        onRetry: () => uploadManager.retryRejectedFile(n.id, r.file),
        onRemove: () =>
          uploadManager.dismissRejectedFiles(
            (rf) =>
              rf.file === r.file &&
              rf.notificationId === n.id &&
              (rf.status ?? 'failed') === 'failed',
          ),
        removeAction: r.createdMedia ? 'delete' : 'dismiss',
      });
    });

    failures.cancelled.forEach((r, i) => {
      rows.push({
        key: `${n.id}:cancel:${i}`,
        filename: r.file.name,
        reason: r.reason,
        status: 'cancelled',
        isAudio: n.isAudio,
        thumbnailUrl: getFileThumbnailUrl(r.file),
        onRemove: () =>
          uploadManager.dismissRejectedFiles(
            (rf) => rf.file === r.file && rf.notificationId === n.id && rf.status === 'cancelled',
          ),
      });
    });

    // Duplicates that were filtered out *before* this batch even tried to
    // upload — they're outcomes of the same user action, so surface them
    // alongside the failures here too. Per-row "View in library" stays the
    // only action; there's nothing to retry or remove.
    if (n.type === 'upload-file' && n.skippedDuplicates) {
      n.skippedDuplicates.forEach((d, i) => {
        rows.push({
          key: `${n.id}:dup:${i}`,
          filename: d.filename,
          reason: 'Already in this library',
          isAudio: false,
          thumbnailUrl: null,
          viewInLibrary:
            d.libraryId && d.mediaId
              ? {
                  libraryId: d.libraryId,
                  mediaId: d.mediaId,
                  // Closes the results modal + the upload panel so the
                  // user lands cleanly on the library listing with the
                  // matched media flashing. Also fires tracking so the
                  // dashboard can split "View in library" by surface.
                  onNavigate: () => {
                    trackingStore.viewInLibraryClicked({
                      source: 'all-results-duplicates',
                      libraryId: d.libraryId!,
                      mediaId: d.mediaId!,
                    });
                    onClose();
                    notificationStore.setPopoverOpen(false);
                  },
                }
              : undefined,
        });
      });
    }

    failures.processingFailures.forEach((p, i) => {
      const filename =
        p.media?.metadata?.name ??
        p.media?.metadata?.filename ??
        n.mediaTitles[n.mediaIds.indexOf(p.mediaId)] ??
        'Untitled';
      rows.push({
        key: `${n.id}:proc:${i}`,
        filename,
        reason: p.reason,
        status: 'failed',
        isAudio: n.isAudio,
        thumbnailUrl: null,
        onRetry: () => uploadManager.retryProcessingFailure(n.id, p.mediaId),
        onRemove: () => {
          const notif = notificationStore.notifications.find((nn) => nn.id === n.id);
          if (notif && isBatchNotification(notif)) {
            const removeIdx = notif.mediaIds.indexOf(p.mediaId);
            if (removeIdx >= 0) {
              notif.mediaIds = notif.mediaIds.filter((_, i) => i !== removeIdx);
              notif.mediaTitles = notif.mediaTitles.filter((_, i) => i !== removeIdx);
            }
            notif.erroredMediaIds = notif.erroredMediaIds.filter((id) => id !== p.mediaId);
          }
        },
      });
    });

    if (rows.length > 0) {
      groups.push({
        key: n.id,
        batchName: n.title,
        batchTimestamp: n.timestamp,
        rows,
      });
    }
  }

  // Newest batch first.
  groups.sort((a, b) => (b.batchTimestamp ?? 0) - (a.batchTimestamp ?? 0));

  const totalRows = groups.reduce((sum, g) => sum + g.rows.length, 0);
  const metaText = totalRows
    ? `${totalRows} ${totalRows === 1 ? 'item needs' : 'items need'} attention${
        filterNotificationId ? ' · filtered' : ''
      }`
    : 'All recent uploads';

  const handleClearAllFailed = () => {
    const pruneErroredPreservingAlignment = (n: BatchNotification) => {
      if (n.erroredMediaIds.length === 0) return;
      const errored = new Set(n.erroredMediaIds);
      const keepIdx: number[] = [];
      for (let i = 0; i < n.mediaIds.length; i++) {
        if (!errored.has(n.mediaIds[i])) keepIdx.push(i);
      }
      n.mediaIds = keepIdx.map((i) => n.mediaIds[i]);
      n.mediaTitles = keepIdx.map((i) => n.mediaTitles[i]);
      n.erroredMediaIds = [];
    };

    if (filterNotificationId) {
      uploadManager.dismissRejectedFiles((r) => r.notificationId === filterNotificationId);
      const notif = notificationStore.notifications.find((n) => n.id === filterNotificationId);
      if (notif && isBatchNotification(notif)) {
        pruneErroredPreservingAlignment(notif);
      }
    } else {
      // Drop only tagged rejects (keep library-scoped untagged rejects from
      // drag-drop validation; those live in the library failed-media modal).
      uploadManager.dismissRejectedFiles((r) => !!r.notificationId);
      for (const n of notificationStore.notifications) {
        if (isBatchNotification(n)) {
          pruneErroredPreservingAlignment(n);
        }
      }
    }
  };

  // `onRetryAll` here fans out to per-row `onRetry` callbacks across the
  // batch's failed entries — so if no row is retryable (e.g. everything is
  // `cancelled` rather than `failed`), the button has nothing to do.
  // Modal's default disabled rule is just "no rows"; we tighten it here
  // for this caller without changing the default for surfaces that use
  // `onRetryAll` as a single batch op (e.g. the notif-bell's "Retry all").
  const hasAnyRetryable = groups.some((g) => g.rows.some((r) => r.onRetry));

  return (
    <FailedMediaListModal
      open={open}
      onClose={onClose}
      title="Upload results"
      metaText={metaText}
      groups={groups}
      onRetryAll={onRetryAll ? () => onRetryAll(filterNotificationId) : undefined}
      retryAllDisabled={totalRows === 0 || !hasAnyRetryable}
      onClearAll={handleClearAllFailed}
    />
  );
});
